import { type ApprovedTheme, type ThemeTokens } from './approved-theme';

const platformTypography = {
  fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSizeBase: '1rem',
  fontSizeSmall: '0.875rem',
  fontSizeLarge: '1.125rem',
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightBold: '700',
  lineHeightBase: '1.5',
} as const;

const platformLayout = {
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  radius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    pill: '999px',
  },
  elevation: {
    sm: '0 1px 2px rgb(15 23 42 / 0.12)',
    md: '0 4px 12px rgb(15 23 42 / 0.16)',
    lg: '0 12px 28px rgb(15 23 42 / 0.22)',
  },
} as const;

function tokens(
  color: ThemeTokens['color'],
  elevation: ThemeTokens['elevation'] = platformLayout.elevation,
): ThemeTokens {
  return {
    color,
    typography: platformTypography,
    spacing: platformLayout.spacing,
    radius: platformLayout.radius,
    elevation,
  };
}

export const APPROVED_THEMES = [
  {
    id: 'corporate',
    name: 'Corporativo',
    colorScheme: 'light',
    tokens: tokens({
      primary: '#005cbb',
      primaryContainer: '#d7e3ff',
      secondary: '#465d91',
      background: '#f7f9fc',
      surface: '#ffffff',
      surfaceVariant: '#e9eef6',
      textPrimary: '#182230',
      textSecondary: '#465568',
      border: '#758195',
      disabled: '#7a8491',
      success: '#146c43',
      warning: '#8a4b00',
      error: '#ba1a1a',
      info: '#005ea8',
    }),
  },
  {
    id: 'light',
    name: 'Claro',
    colorScheme: 'light',
    tokens: tokens({
      primary: '#6d28d9',
      primaryContainer: '#ede9fe',
      secondary: '#0f766e',
      background: '#fff8ed',
      surface: '#fffdf8',
      surfaceVariant: '#f2e8d9',
      textPrimary: '#261a2f',
      textSecondary: '#62536b',
      border: '#796b80',
      disabled: '#817887',
      success: '#087f5b',
      warning: '#8f4e00',
      error: '#b42318',
      info: '#075985',
    }),
  },
  {
    id: 'dark',
    name: 'Oscuro',
    colorScheme: 'dark',
    tokens: tokens(
      {
        primary: '#a8c7fa',
        primaryContainer: '#284b7c',
        secondary: '#8fd5ca',
        background: '#0f1720',
        surface: '#17212b',
        surfaceVariant: '#24313d',
        textPrimary: '#f3f6fa',
        textSecondary: '#bac7d5',
        border: '#8190a0',
        disabled: '#87939f',
        success: '#72d5a5',
        warning: '#ffbf69',
        error: '#ffb4ab',
        info: '#8ecaff',
      },
      {
        sm: '0 1px 2px rgb(0 0 0 / 0.35)',
        md: '0 4px 12px rgb(0 0 0 / 0.45)',
        lg: '0 12px 28px rgb(0 0 0 / 0.55)',
      },
    ),
  },
] as const satisfies readonly ApprovedTheme[];

export const DEFAULT_THEME = APPROVED_THEMES[0];
